/**
 * Regular Meetings prototype — data shared by the three views (a committee,
 * the Governance Committee, staff).
 *
 * Committees, roles, people and owned policies are the real ones from the Hub
 * (committees / assignments / policy_unit, Oct 2026), so reviewers aren't
 * distracted by made-up names. Meeting dates, statuses, minutes and reports
 * are sample.
 *
 * The focus is committees. Chapters and communities sit under the Global
 * Chapter Development Committee (Global CDC → regional CDCs → chapters →
 * communities) and appear only through it.
 */

export type GroupType = "Board" | "Board committee" | "Committee of the Association";
export type LastStatus = "Report sent" | "Waiting for minutes" | "Recording missing" | "Did it happen?";
export type Light = "green" | "amber" | "red";

export interface Report {
    meeting: string;
    received: string;
    read: boolean;
    updates: string;
    decisions: string;
    risks: string;
    projects: { name: string; light: Light }[];
}

export interface Region { name: string; chapters: number; communities: number; chair: string | null }

export interface Group {
    id: string;
    name: string;
    type: GroupType;
    chair: string | null;
    viceChair?: string;
    liaison: string;
    members: number;
    /** Real policies this committee owns in the Policy Engine. */
    policies: string[];
    /** This year so far (sample). */
    planned: number;
    held: number;
    due: number;
    onTime: number;
    lastMeeting: string;
    lastStatus: LastStatus;
    daysWaiting: number;
    nextMeeting: string | null;
    nextYear: "Approved" | "Waiting for the Chair" | "Not started";
    report: Report | null;
    /** Only the Global CDC: the regional committees, chapters and communities under it. */
    covers?: { regions: Region[]; unassignedChapters: number };
}

const r = (meeting: string, received: string, updates: string, decisions: string, risks: string, projects: Report["projects"], read = true): Report =>
    ({ meeting, received, read, updates, decisions, risks, projects });

export const initialGroups: Group[] = [
    // ── Board and board committees ────────────────────────────────────────
    {
        id: "board", name: "Board of Directors", type: "Board", chair: "John Kolovos", viceChair: "Kelly Corbiere", liaison: "Tyler Wood", members: 18,
        policies: ["Board of Directors Code of Conduct and Fiduciary Duties", "Executive Compensation and Performance Review", "Antitrust and Fair Competition Policy", "Trademark, Intellectual Property, and Credential Usage", "Peer Institution Alliance and Mutual Recognition Policy"],
        planned: 6, held: 5, due: 5, onTime: 5, lastMeeting: "24 Sep", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "12 Nov", nextYear: "Approved",
        report: r("24 Sep", "25 Sep", "Approved the FY27 budget outline.", "None", "None raised.", [{ name: "FY27 budget", light: "green" }]),
    },
    {
        id: "exec", name: "Executive Committee", type: "Board committee", chair: "John Kolovos", liaison: "Tyler Wood", members: 4,
        policies: ["Executive Committee Authority and Delegation"],
        planned: 6, held: 5, due: 5, onTime: 5, lastMeeting: "8 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "5 Nov", nextYear: "Approved",
        report: r("8 Oct", "9 Oct", "Reviewed the agenda for the November Board meeting.", "None", "None raised.", [{ name: "Board agenda", light: "green" }]),
    },
    {
        id: "fin", name: "Finance Committee", type: "Board committee", chair: "Eric Caisse", liaison: "Heather Higgins", members: 7,
        policies: ["Financial Internal Controls and Delegation of Authority", "Procurement and Vendor Management Policy", "Travel and Expense Reimbursement Policy", "Operating Reserve and Investment Policy", "Gift Acceptance Policy", "Tax and Cross-Border Surcharge Policy"],
        planned: 6, held: 5, due: 5, onTime: 4, lastMeeting: "14 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "11 Nov", nextYear: "Approved",
        report: r("14 Oct", "16 Oct", "Q3 results reviewed; reserves within policy.", "Board: approve the revised travel and expense limits.", "None raised.", [{ name: "FY27 budget", light: "green" }, { name: "Expense policy update", light: "amber" }], false),
    },
    {
        id: "audit", name: "Audit Committee", type: "Board committee", chair: "Ananda Bhaumik", liaison: "Tyler Wood", members: 4,
        policies: ["Whistleblower Protection and Corporate Transparency", "Document Retention and Destruction Policy"],
        planned: 4, held: 3, due: 3, onTime: 3, lastMeeting: "30 Sep", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "9 Dec", nextYear: "Waiting for the Chair",
        report: r("30 Sep", "1 Oct", "Audit firm engagement letter reviewed.", "None", "None raised.", [{ name: "FY26 audit", light: "green" }]),
    },
    {
        id: "gc", name: "Governance Committee", type: "Board committee", chair: "Cedric Thompson", liaison: "Joel Pannikot", members: 5,
        policies: ["Nominating, Election, and Board Vacancy Procedure", "Conflict of Interest and Related-Party Transactions", "Volunteer Engagement Policy (Groups vs. Gigs)", "Group Charters and Universal Operational Guidelines", "Volunteer Onboarding, Code of Conduct, and Agreements", "Volunteer Safeguarding, Diversity, and Anti-Harassment", "Values and Culture Integration Policy", "Adverse Action Notice Standard"],
        planned: 10, held: 8, due: 8, onTime: 7, lastMeeting: "21 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "18 Nov", nextYear: "Approved",
        report: r("21 Oct", "22 Oct", "Started the Regular Meetings pilot; first recorded meeting went end to end.", "Board: approve the one-page group report format.", "Some committees have no Chair in post.", [{ name: "Regular meetings", light: "green" }, { name: "Charter review", light: "amber" }]),
    },
    // ── Committees of the Association ─────────────────────────────────────
    {
        id: "adm", name: "Admissions Committee", type: "Committee of the Association", chair: "Greg Harmon", viceChair: "Oliver Patrick Mathis", liaison: "Barbara Terry", members: 15,
        policies: ["Membership Eligibility, Admissions, and Sponsorship", "Designation Maintenance, Dues, and Hardship Waivers"],
        planned: 10, held: 8, due: 8, onTime: 8, lastMeeting: "15 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "12 Nov", nextYear: "Approved",
        report: r("15 Oct", "16 Oct", "Six applications reviewed; four recommended to the Board.", "Board: ratify four Professional Member admissions.", "None raised.", [{ name: "Application reviews", light: "green" }]),
    },
    {
        id: "ctc", name: "Curriculum & Testing Committee (CTC)", type: "Committee of the Association", chair: "Fabien Ouellette", viceChair: "Keith Dubauskas", liaison: "Mike Rickert", members: 11,
        policies: ["Curriculum Development and Practice Analysis", "Examination Security, Confidentiality, and Item Writing", "Credentialing Volunteer Qualifications and Remuneration Policy", "Candidate Eligibility, Identification, and Test Administration", "Remote Proctoring Environmental Policy", "Candidate Accommodations (ADA) and Accessibility", "Exam Grading, Standard Setting, and Appeals Policy", "Exam Deferral, Cancellation, Retake, and Refund Policy", "Third-Party Preparatory Provider and Vendor Relations"],
        planned: 6, held: 4, due: 4, onTime: 3, lastMeeting: "8 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "12 Nov", nextYear: "Not started",
        report: r("8 Oct", "9 Oct", "Level III item bank review under way.", "None", "None raised.", [{ name: "Item bank review", light: "green" }]),
    },
    {
        id: "es", name: "Ethics & Standards Committee (E&S)", type: "Committee of the Association", chair: "Foram Chheda", viceChair: "Milan Vaishnav", liaison: "Mike Rickert", members: 5,
        policies: ["Professional Conduct Statement (PCS) & Duty to Inform", "Ethics Enforcement, Adjudication, and Discipline"],
        planned: 4, held: 3, due: 3, onTime: 3, lastMeeting: "30 Sep", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "2 Dec", nextYear: "Approved",
        report: r("30 Sep", "1 Oct", "Two matters reviewed (details kept within the committee).", "None", "None raised.", [{ name: "Case reviews", light: "green" }]),
    },
    {
        id: "gcdc", name: "Global Chapter Development Committee", type: "Committee of the Association", chair: "Gina Martin Adams", liaison: "Joel Pannikot", members: 4,
        policies: ["Geographic Structure Policy (Chapters vs. Communities)"],
        planned: 6, held: 5, due: 5, onTime: 4, lastMeeting: "13 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "10 Nov", nextYear: "Approved",
        report: r("13 Oct", "15 Oct", "Regional reports in from APAC, EMEA and AMER; LATAM due next month.", "None", "Regional committees in APAC and EMEA have no Chair yet.", [{ name: "Regional structure", light: "amber" }, { name: "Chapter reporting", light: "green" }]),
        covers: {
            regions: [
                { name: "Asia-Pacific (APAC) Chapter Development Committee", chapters: 11, communities: 22, chair: null },
                { name: "Europe, Middle-East and Africa (EMEA) Chapter Development Committee", chapters: 17, communities: 12, chair: null },
                { name: "Latin America (LATAM) Chapter Development Committee", chapters: 5, communities: 4, chair: null },
                { name: "North America (AMER) Chapter Development Committee", chapters: 1, communities: 8, chair: "David Keller" },
            ],
            unassignedChapters: 11,
        },
    },
    {
        id: "tdic", name: "Technology & Data Intelligence Committee", type: "Committee of the Association", chair: "Gina Martin Adams", viceChair: "Mohit Handa", liaison: "Heather Higgins", members: 4,
        policies: ["Data Privacy, Protection, and Global Compliance", "Information Security and Acceptable Use Policy", "Artificial Intelligence (AI) Acceptable Use and Security"],
        planned: 6, held: 5, due: 5, onTime: 3, lastMeeting: "19 Oct", lastStatus: "Waiting for minutes", daysWaiting: 5, nextMeeting: "16 Nov", nextYear: "Waiting for the Chair",
        report: r("21 Sep", "24 Sep", "AI acceptable-use draft circulated for comment.", "None", "None raised.", [{ name: "AI policy", light: "amber" }]),
    },
    {
        id: "aoc", name: "Academia Outreach Committee", type: "Committee of the Association", chair: "Yehonatan Jacobs", viceChair: "Hitesh Punjabi", liaison: "Kaizad Marolia", members: 7,
        policies: ["Academic Partner Program (APP) & University Relations", "Campus Ambassador and Student Engagement Safeguarding", "Global Investment Challenge Ruleset & Operations"],
        planned: 6, held: 5, due: 5, onTime: 5, lastMeeting: "6 Oct", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "1 Dec", nextYear: "Approved",
        report: r("6 Oct", "7 Oct", "Fall Investment Challenge launched with 82 partner universities.", "None", "None raised.", [{ name: "Investment Challenge", light: "green" }], false),
    },
    {
        id: "ioc", name: "Industry Outreach Committee", type: "Committee of the Association", chair: null, liaison: "Tyler Wood", members: 3,
        policies: ["Advocacy, Regulatory Engagement, and Lobbying", "Corporate Sponsorship and Employer Partnership Policy", "Member Ambassador and Evidence-Based Outreach Policy", "Career Services and Recruitment Disclaimer Policy", "Media Outreach, Co-Branding, and Spokesperson Policy"],
        planned: 0, held: 1, due: 1, onTime: 0, lastMeeting: "3 Jun", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: null, nextYear: "Not started",
        report: null,
    },
    {
        id: "jta", name: "Journal of Technical Analysis Committee", type: "Committee of the Association", chair: "Sergio Santamaria", liaison: "Tom Bruni", members: 9,
        policies: ["Editorial Guidelines and Publication Standards"],
        planned: 4, held: 3, due: 4, onTime: 2, lastMeeting: "29 Sep", lastStatus: "Recording missing", daysWaiting: 24, nextMeeting: "8 Dec", nextYear: "Not started",
        report: null,
    },
    {
        id: "awards", name: "Awards Committee", type: "Committee of the Association", chair: "Ralph Acampora", liaison: "Joel Pannikot", members: 3,
        policies: ["Annual Awards Nomination and Selection Policy"],
        planned: 3, held: 2, due: 2, onTime: 2, lastMeeting: "17 Sep", lastStatus: "Report sent", daysWaiting: 0, nextMeeting: "19 Nov", nextYear: "Waiting for the Chair",
        report: r("17 Sep", "18 Sep", "Nominations open for the 2027 awards.", "None", "None raised.", [{ name: "2027 nominations", light: "green" }]),
    },
    {
        id: "dow", name: "Charles H. Dow Award Committee", type: "Committee of the Association", chair: "Sandra Stoutenburg", liaison: "Alayna Scott", members: 7,
        policies: [],
        planned: 4, held: 2, due: 3, onTime: 2, lastMeeting: "15 Oct", lastStatus: "Did it happen?", daysWaiting: 8, nextMeeting: "10 Dec", nextYear: "Approved",
        report: r("20 Aug", "22 Aug", "Judging panel confirmed for the 2027 award.", "None", "None raised.", [{ name: "2027 award", light: "green" }]),
    },
];

/** What isn't right for a committee, in plain words. Used by every view. */
export function issues(g: Group): string[] {
    const out: string[] = [];
    if (!g.chair) out.push("No Chair in post");
    if (g.planned < 4) out.push(g.planned === 0 ? "No meetings planned this year" : `Only ${g.planned} meetings planned (minimum is 4)`);
    if (g.lastStatus === "Recording missing") out.push(`No record of the ${g.lastMeeting} meeting for ${g.daysWaiting} days`);
    if (g.lastStatus === "Waiting for minutes" && g.daysWaiting > 3) out.push(`Minutes from ${g.lastMeeting} not confirmed after ${g.daysWaiting} days`);
    if (g.lastStatus === "Did it happen?") out.push(`Not sure the ${g.lastMeeting} meeting took place`);
    if (!g.report) out.push("No report this quarter");
    if (g.nextYear === "Not started") out.push("2027 not planned yet");
    if (g.covers?.unassignedChapters) out.push(`${g.covers.unassignedChapters} chapters not yet under a regional committee`);
    return out;
}

export type Health = "On track" | "Needs attention" | "Off track";

export function health(g: Group): Health {
    const n = issues(g).length;
    return n === 0 ? "On track" : n === 1 ? "Needs attention" : "Off track";
}

// ─── The example committee: Curriculum & Testing Committee ────────────────────

export const EXAMPLE = {
    id: "ctc",
    name: "Curriculum & Testing Committee",
    chair: "Fabien Ouellette",
    viceChair: "Keith Dubauskas",
    liaison: "Mike Rickert",
    member: "Trent Smalley",
    members: ["Fabien Ouellette (Chair)", "Keith Dubauskas (Vice Chair)", "Tom Schneider (Vice Chair)", "Trent Smalley", "Stephen Donovan", "Dave Tonaszuck", "Gregory Cooper", "Shawn Keel"],
};

/** Where the example committee is in its cycle. The prototype lets you jump between these. */
export type GroupStage = "plan" | "before" | "after" | "done";

export const NEXT_MEETING = {
    date: "Thursday 12 November",
    time: "12:00–1:00 pm ET",
    agenda: [
        { item: "December exams: results timeline", why: "Standing item before every exam window" },
        { item: "Level III item writers", why: "Trent's task from the last meeting" },
        { item: "Annual review: Exam Deferral, Cancellation, Retake, and Refund Policy", why: "Policy review due — the committee owns 9 policies" },
        { item: "2027 meeting dates", why: "Check everyone can make Thursday lunchtimes" },
    ],
};

export const DRAFT = {
    attended: 6,
    names: ["Fabien Ouellette", "Keith Dubauskas", "Tom Schneider", "Trent Smalley", "Stephen Donovan", "Gregory Cooper"],
    decisions: [
        "Publish December results within six weeks of the exam.",
        "No changes to the Exam Deferral, Cancellation, Retake, and Refund Policy this year; send the review to the Governance Committee.",
    ],
    tasks: [
        { text: "Confirm six Level III item writers", owner: "Trent Smalley", due: "27 Nov" },
        { text: "Draft the results-timeline notice for candidates", owner: "Keith Dubauskas", due: "20 Nov" },
        { text: "Send the policy review outcome to the Governance Committee", owner: "Fabien Ouellette", due: "30 Nov" },
    ],
    check: { question: "Who will brief the new item writers?", options: ["Stephen Donovan", "Gregory Cooper", "Nobody — leave it out"] },
};

export const PAST_OUTCOMES = [
    {
        date: "Thu 8 October",
        attended: "7 of 8",
        decisions: ["Start the Level III item bank review.", "Ask for six more item writers."],
        tasks: [{ text: "Find Level III item writers", owner: "Trent Smalley", done: true }],
    },
    {
        date: "Thu 13 August",
        attended: "6 of 8",
        decisions: ["Keep remote proctoring for all three levels.", "Move meetings to Thursday lunchtimes."],
        tasks: [{ text: "Update the remote proctoring notice", owner: "Keith Dubauskas", done: true }],
    },
];

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Dates spread evenly through the year, each on the chosen weekday. */
export function suggestDates(year: number, weekday: number, count: number): string[] {
    const DAY = 86_400_000;
    const start = Date.UTC(year, 0, 1);
    const span = (Date.UTC(year + 1, 0, 1) - start) / DAY;
    return Array.from({ length: Math.max(0, count) }, (_, i) => {
        const center = start + Math.round((i + 0.5) * (span / count)) * DAY;
        let diff = weekday - new Date(center).getUTCDay();
        if (diff > 3) diff -= 7;
        if (diff < -3) diff += 7;
        return new Date(center + diff * DAY).toLocaleDateString("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" });
    });
}

export interface ActivityItem { at: string; text: string }

export const initialActivity: ActivityItem[] = [
    { at: "Today 09:12", text: "Governance Committee report received" },
    { at: "Yesterday", text: "Reminder sent to Gina Martin Adams (Technology & Data Intelligence Committee) about the 19 Oct minutes" },
    { at: "21 Oct", text: "Governance Committee meeting recorded — 4 of 5 present" },
    { at: "15 Oct", text: "Charles H. Dow Award Committee: no recording received; Chair asked whether it took place" },
    { at: "15 Oct", text: "Global Chapter Development Committee report received (covers APAC, EMEA and AMER)" },
];

export const PROJECTS = [
    { name: "Regular meetings", status: "Live", note: "Every committee plans its year, meets, confirms minutes and reports." },
    { name: "Onboarding", status: "Next", note: "Code of conduct, a starter pack and a welcome call for new volunteers." },
    { name: "Recruiting volunteers", status: "Planned", note: "Seats and terms ahead, and finding people to fill them." },
    { name: "Policy upkeep", status: "Planned", note: "Committees review the 62 policies they own every year." },
];

/** The Chair's name, or a plain note when the seat is empty. */
export const chairOf = (g: Group) => g.chair ?? "No Chair yet";

/** Who a reminder goes to: the Chair, or the staff liaison when there is no Chair. */
export const remindTarget = (g: Group) => g.chair ?? `${g.liaison} (staff liaison)`;

/** One line for the Global CDC: what reports through it. */
export const coversLine = (g: Group) => {
    if (!g.covers) return "";
    const ch = g.covers.regions.reduce((n, r) => n + r.chapters, 0) + g.covers.unassignedChapters;
    const co = g.covers.regions.reduce((n, r) => n + r.communities, 0);
    return `covers ${g.covers.regions.length} regional committees · ${ch} chapters · ${co} communities`;
};
